import { useEffectWithController } from '@carvana/merch-react-hooks';
import useSearchBarEvents from './useSearchBarEvents';
import { GET_SUGGESTIONS_V4, SearchType } from '../constants';
import { useInputContext } from '../context/InputContext';
import { SearchSuggestion } from '../contracts';
import { normalizeFetchedSuggestions } from '../helpers';
import { useSearchBarContext } from '../SearchBarContext';
import { useSuggestionsContext } from '../SuggestionsContext';

export default function useFetchSuggestions() {
  const { setNoResults, suggestionQuery } = useInputContext();
  const { apiBaseUrl, onSuggestApiError, trackEvent } = useSearchBarContext();
  const searchBarEvents = useSearchBarEvents(trackEvent);

  const {
    suggestions: { setData }
  } = useSuggestionsContext();

  useEffectWithController(
    controller => {
      async function fetchData() {
        if (suggestionQuery.length === 0) {
          setNoResults(false);
        }

        if (!suggestionQuery) {
          setData([]);

          return;
        }

        try {
          const response = await fetch(
            `${apiBaseUrl}${GET_SUGGESTIONS_V4}?query=${suggestionQuery}`,
            { signal: controller.signal }
          );

          if (!response.ok) {
            throw new Error(response.statusText);
          }

          const { suggestions }: { suggestions: SearchSuggestion[] } =
            await response.json();

          if (suggestions.length === 0 && suggestionQuery.length > 0) {
            setNoResults(true);
            searchBarEvents.noResults(suggestionQuery);
          } else {
            setNoResults(false);
          }

          setData(
            normalizeFetchedSuggestions({
              inputValue: suggestionQuery,
              searchType: SearchType.TextSearch,
              data: suggestions
            })
          );
        } catch (err: unknown) {
          if (controller.signal.aborted) return;

          if (onSuggestApiError) {
            onSuggestApiError(err);
          } else {
            console.error(err);
          }

          setData([]);
        }
      }

      void fetchData();
    },
    [
      apiBaseUrl,
      onSuggestApiError,
      searchBarEvents,
      setData,
      setNoResults,
      suggestionQuery
    ]
  );
}
